/**
 * @file AllDayDrawer.tsx
 * @description Slide-over summary showing total portions of each dish required across active orders.
 */

import React from 'react';
import { X, Layers, Flame } from 'lucide-react';
import { calculateAllDayCounts } from '../services/allDayCalculator.service';
import type { KitchenOrder, KitchenStation } from '../../../types/order.types';

interface AllDayDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orders: KitchenOrder[];
  selectedStation: KitchenStation;
}

export const AllDayDrawer: React.FC<AllDayDrawerProps> = ({
  isOpen,
  onClose,
  orders,
  selectedStation,
}) => {
  if (!isOpen) return null;

  const items = calculateAllDayCounts(orders, selectedStation);
  const totalPortions = items.reduce((sum, item) => sum + item.totalQuantity, 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md h-full bg-zinc-950 border-l border-zinc-800 flex flex-col shadow-2xl">
        {/* Drawer Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white">ALL DAY PRODUCTION</h2>
              <p className="text-xs text-zinc-400">
                {totalPortions} total portions needed across {orders.length} tickets
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Item List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 text-sm">
              No active items needed in production right now.
            </div>
          ) : (
            items.map(item => (
              <div
                key={item.itemId}
                className="p-3 rounded-xl bg-zinc-900/70 border border-zinc-800/80 flex items-center justify-between hover:border-zinc-700 transition-colors"
              >
                <div className="min-w-0 pr-2">
                  <p className="font-bold text-sm text-zinc-100 truncate">{item.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    From {item.orderCount} active ticket{item.orderCount > 1 ? 's' : ''} • {item.station}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.totalQuantity >= 10 && (
                    <Flame className="w-4 h-4 text-rose-500 fill-rose-500 animate-pulse" />
                  )}
                  <span className="inline-flex items-center justify-center min-w-[36px] h-9 px-2 rounded-lg bg-amber-500 text-black font-mono font-black text-base shadow-md shadow-amber-500/10">
                    {item.totalQuantity}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900/40 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 min-h-[46px] rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-sm active:scale-[0.98] transition-all cursor-pointer"
          >
            Close All Day View
          </button>
        </div>
      </div>
    </div>
  );
};
